import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DRLogo from "../MyImages/DRLogo.png";

export default function Confirmation() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const name = state?.name || "músico";
  const id = state?.id;

  const fadeUp = {
    hidden: { opacity: 0, y: 16, filter: "blur(10px)" },
    show: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.85, ease: "easeOut" },
    },
  };

  const softIn = {
    hidden: { opacity: 0, y: 10, scale: 0.99, filter: "blur(12px)" },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      filter: "blur(0px)",
      transition: { duration: 0.85, ease: "easeOut" },
    },
  };

  return (
    <div className="min-h-screen w-full bg-white text-slate-900 overflow-hidden">
      {/* Background (same vibe as form) */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-[#EAF6FF] via-[#FFF3EC] to-[#F3FFF7]" />
        <div className="absolute -top-28 -left-32 h-[520px] w-[520px] rounded-full bg-blue-500/18 blur-3xl" />
        <div className="absolute -bottom-28 -right-32 h-[560px] w-[560px] rounded-full bg-red-500/14 blur-3xl" />
        <div className="absolute top-[35%] left-[55%] h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-amber-300/22 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.08] bg-[repeating-linear-gradient(135deg,_rgba(15,23,42,0.45)_0px,_rgba(15,23,42,0.45)_1px,_transparent_1px,_transparent_16px)]" />
      </div>

      <div className="relative z-10">
        {/* Top bar */}
        <nav className="w-full">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-6 md:px-10 py-5">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="text-sm md:text-base font-semibold tracking-[0.22em]"
            >
              SIMANOF FESTIVAL
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.08 }}
              className="flex items-center gap-5 text-xs md:text-sm tracking-wide"
            >
              <button
                onClick={() => navigate("/")}
                className="text-slate-700 hover:text-slate-900 transition"
              >
                Inicio
              </button>
            </motion.div>
          </div>

          <div className="absolute inset-x-0 top-0 h-[74px] -z-10 bg-white/70 backdrop-blur-md border-b border-white/70" />
        </nav>

        {/* Content */}
        <div className="mx-auto max-w-4xl px-6 md:px-10 pt-10 pb-16">
          {/* Logo + intro */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="flex flex-col items-center text-center"
          >
            <img
              src={DRLogo}
              alt="Logo Festival República Dominicana"
              className="h-24 md:h-32 lg:h-40 w-auto drop-shadow-[0_22px_70px_rgba(0,0,0,0.12)]"
            />

            <div className="mt-6 inline-flex items-center gap-3 rounded-full border border-white/70 bg-white/75 px-4 py-2 backdrop-blur-md shadow-sm">
              <span className="h-2 w-2 rounded-full bg-green-600" />
              <span className="text-xs tracking-[0.30em] text-slate-700">
                APLICACIÓN • RECIBIDA
              </span>
            </div>

            <h1 className="mt-6 text-3xl md:text-5xl font-light tracking-wide">
              ¡Solicitud confirmada!
            </h1>

            <p className="mt-4 text-slate-700 text-sm md:text-base max-w-2xl leading-relaxed">
              Gracias, <span className="text-slate-900 font-semibold">{name}</span>. Recibimos tu aplicación
              correctamente. Nuestro equipo revisará tu video de audición y tu información.
            </p>

            <div className="mt-8 h-px w-full bg-gradient-to-r from-transparent via-blue-600/20 to-transparent" />
            <div className="-mt-px h-px w-full bg-gradient-to-r from-transparent via-red-600/15 to-transparent" />
          </motion.div>

          {/* Main card */}
          <motion.div
            variants={softIn}
            initial="hidden"
            animate="show"
            className="mt-10 rounded-[34px] border border-white/70 bg-white/75 backdrop-blur-md shadow-[0_22px_80px_rgba(2,6,23,0.08)] overflow-hidden"
          >
            <div className="p-7 md:p-10">
              {/* ID */}
              {id && (
                <div className="rounded-2xl border border-slate-200 bg-white/70 backdrop-blur-md px-5 py-4 shadow-sm">
                  <p className="text-xs tracking-[0.30em] text-slate-600">
                    CONFIRMATION ID
                  </p>
                  <p className="mt-2 text-slate-900 text-sm">
                    <span className="font-mono">{id}</span>
                  </p>
                </div>
              )}

              {/* What happens next */}
              <div className="mt-6 rounded-2xl border border-slate-200 bg-white/70 backdrop-blur-md px-5 py-5 shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                  <p className="text-sm tracking-wide text-slate-800">
                    Próximos pasos
                  </p>
                </div>

                <div className="mt-4 grid gap-3 text-sm text-slate-700 leading-relaxed">
                  <div className="flex items-start gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-blue-600" />
                    <p>
                      Revisaremos tu video (2 escalas + etude / concierto actual) junto con tu información.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-red-600" />
                    <p>
                      Los resultados de la audición serán publicados <b>a mediados de abril</b>.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-slate-500" />
                    <p>
                      Si necesitamos algo adicional, nos comunicaremos contigo por email.
                    </p>
                  </div>
                </div>

                <div className="mt-6 h-[10px] w-full rounded-full bg-gradient-to-r from-blue-600/65 via-amber-400/45 to-red-600/55" />
              </div>

              {/* Buttons */}
              <div className="mt-10 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
                <button
                  onClick={() => navigate("/")}
                  className="inline-flex items-center justify-center rounded-md px-10 py-3.5
                             border border-slate-300 bg-white/70 backdrop-blur-md
                             hover:bg-white hover:border-slate-400
                             transition duration-300 tracking-[0.22em] text-xs md:text-sm text-slate-900
                             transform hover:scale-[1.04] active:scale-[0.98]"
                >
                  VOLVER AL INICIO
                </button>

                <button
                  onClick={() => navigate("/tour-form")}
                  className="inline-flex items-center justify-center rounded-md px-10 py-3.5
                             border border-slate-300 bg-white/40 backdrop-blur-md
                             hover:bg-white/70 hover:border-slate-400
                             transition duration-300 tracking-[0.22em] text-xs md:text-sm text-slate-900
                             transform hover:scale-[1.04] active:scale-[0.98]"
                >
                  ENVIAR OTRA APLICACIÓN
                </button>
              </div>

              <p className="mt-8 text-xs tracking-[0.26em] text-slate-500 text-center">
                SIMANOF • REPÚBLICA DOMINICANA • ITINERARIO SUJETO A CAMBIOS
              </p>
            </div>

            <div className="h-[5px] w-full bg-gradient-to-r from-blue-600 via-amber-400 to-red-600" />
          </motion.div>
        </div>
      </div>
    </div>
  );
}